import { Controller } from "@hotwired/stimulus"

// Форма продажи: поиск товара с подсказками, строки, итоги.
//
// Строки создаются и удаляются на лету, поэтому состояние живёт не в targets
// (они контроллер-широкие), а в самих элементах строки — через data-role.
// Единственный источник правды по строке — её скрытый input с variant_id.
export default class extends Controller {
  static targets = ["container", "template", "total", "count", "submit"]
  static values = {
    searchUrl: String,
    currency: { type: String, default: "lei" },
  }

  connect() {
    this.nextIndex = 1
    this.searchTimers = new WeakMap()
    this.closeOnOutsideClick = this.closeAllSuggestions.bind(this)
    document.addEventListener("click", this.closeOnOutsideClick)
    this.recalculate()
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
  }

  // --- Публичная точка входа -------------------------------------------
  // Ею пользуется и выбор из подсказок, и (в будущем) кнопки «часто продаётся».
  addVariant({ id, title, price, stock }) {
    const existing = this.rows.find((row) => this.field(row, "variant-id").value === String(id))

    if (existing) {
      // Повторный выбор того же товара наращивает количество, а не плодит
      // вторую строку: иначе построчная проверка остатка пропустит сумму,
      // которая складу не по силам, и сервер отвергнет всю продажу целиком.
      const quantity = this.field(existing, "quantity")
      quantity.value = this.formatQuantity(this.parse(quantity.value) + 1)
      this.recalculate()
      quantity.focus()
      return
    }

    const row = this.rows.find((row) => !this.field(row, "variant-id").value) || this.appendRow()
    this.fill(row, { id, title, price, stock })
  }

  addFromChip(event) {
    event.preventDefault()
    const chip = event.currentTarget
    this.addVariant({
      id: chip.dataset.variantId,
      title: chip.dataset.title,
      price: chip.dataset.price,
      stock: chip.dataset.stock,
    })
  }

  // --- Поиск ------------------------------------------------------------

  query(event) {
    const row = this.rowOf(event.target)
    clearTimeout(this.searchTimers.get(row))
    this.searchTimers.set(row, setTimeout(() => this.search(row), 200))
  }

  async search(row) {
    const term = this.field(row, "query").value.trim()
    const suggest = this.field(row, "suggest")
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(term)}`

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) return
      suggest.innerHTML = await response.text()
      suggest.classList.remove("hidden")
    } catch {
      // Сеть моргнула — молча оставляем прошлые подсказки, чтобы касса
      // не сыпала ошибками посреди продажи.
    }
  }

  pick(event) {
    const button = event.currentTarget
    const row = this.rowOf(button)
    this.closeSuggestions(row)
    this.fill(row, {
      id: button.dataset.variantId,
      title: button.dataset.title,
      price: button.dataset.price,
      stock: button.dataset.stock,
    })
  }

  keydown(event) {
    if (event.key === "Escape") this.closeSuggestions(this.rowOf(event.target))
  }

  // --- Строки -----------------------------------------------------------

  addRow(event) {
    event.preventDefault()
    this.appendRow().querySelector("[data-role='query']").focus()
  }

  removeRow(event) {
    event.preventDefault()
    const row = this.rowOf(event.target)
    if (this.rows.length > 1) {
      row.remove()
    } else {
      this.reset(row)
    }
    this.recalculate()
  }

  increment(event) {
    event.preventDefault()
    this.stepQuantity(this.rowOf(event.target), 1)
  }

  decrement(event) {
    event.preventDefault()
    this.stepQuantity(this.rowOf(event.target), -1)
  }

  // --- Пересчёт ---------------------------------------------------------

  recalculate() {
    let total = 0
    let filled = 0

    this.rows.forEach((row) => {
      const price = this.parse(row.dataset.price)
      const stock = this.parse(row.dataset.stock)
      const quantity = this.parse(this.field(row, "quantity").value)
      const chosen = Boolean(this.field(row, "variant-id").value)

      if (chosen && quantity > 0) {
        total += price * quantity
        filled += 1
      }

      this.field(row, "subtotal").textContent = chosen
        ? `${this.formatMoney(price * quantity)} ${this.currencyValue}`
        : "—"

      this.showWarning(row, chosen && quantity > stock ? "over" : null)
    })

    this.totalTarget.textContent = this.formatMoney(total)
    this.countTarget.textContent = filled
    // Кнопка гаснет, если продавать нечего или строка просит больше остатка.
    // Это только подсказка: настоящая защита от перепродажи — блокировка
    // остатка в OrderCreator, остаток в разметке протухает от чужой продажи.
    this.submitTarget.disabled = filled === 0 || this.hasOverdraft
  }

  validate(event) {
    if (this.submitTarget.disabled) event.preventDefault()
  }

  // --- Внутреннее -------------------------------------------------------

  get rows() {
    return Array.from(this.containerTarget.querySelectorAll("[data-row]"))
  }

  get hasOverdraft() {
    return this.rows.some((row) => {
      if (!this.field(row, "variant-id").value) return false
      return this.parse(this.field(row, "quantity").value) > this.parse(row.dataset.stock)
    })
  }

  rowOf(element) {
    return element.closest("[data-row]")
  }

  field(row, role) {
    return row.querySelector(`[data-role='${role}']`)
  }

  appendRow() {
    const html = this.templateTarget.innerHTML.replaceAll("NEW_INDEX", this.nextIndex++)
    this.containerTarget.insertAdjacentHTML("beforeend", html)
    return this.containerTarget.lastElementChild
  }

  fill(row, { id, title, price, stock }) {
    row.dataset.price = price
    row.dataset.stock = stock
    this.field(row, "variant-id").value = id
    this.field(row, "query").value = title
    this.field(row, "price").textContent = `${this.formatMoney(this.parse(price))} ${this.currencyValue}`
    this.field(row, "stock").textContent = this.formatQuantity(this.parse(stock))

    const quantity = this.field(row, "quantity")
    if (this.parse(quantity.value) <= 0) quantity.value = "1"

    this.recalculate()
    quantity.focus()
    quantity.select()
  }

  reset(row) {
    delete row.dataset.price
    delete row.dataset.stock
    this.field(row, "variant-id").value = ""
    this.field(row, "query").value = ""
    this.field(row, "quantity").value = "1"
    this.field(row, "price").textContent = "—"
    this.field(row, "stock").textContent = "—"
    this.field(row, "subtotal").textContent = "—"
    this.showWarning(row, null)
  }

  stepQuantity(row, delta) {
    const input = this.field(row, "quantity")
    input.value = this.formatQuantity(Math.max(0, this.parse(input.value) + delta))
    this.recalculate()
  }

  showWarning(row, kind) {
    const badge = this.field(row, "warning")
    if (!kind) {
      badge.classList.add("hidden")
      return
    }
    badge.textContent = badge.dataset.overText || "!"
    badge.classList.remove("hidden")
  }

  closeSuggestions(row) {
    const suggest = this.field(row, "suggest")
    suggest.classList.add("hidden")
    suggest.innerHTML = ""
  }

  closeAllSuggestions(event) {
    this.rows.forEach((row) => {
      if (!row.contains(event.target)) this.closeSuggestions(row)
    })
  }

  // Запятая как разделитель — привычка кассы, точка — то, что понимает JS.
  parse(value) {
    const number = parseFloat(String(value ?? "").replace(",", "."))
    return Number.isFinite(number) ? number : 0
  }

  formatMoney(value) {
    return value.toFixed(2)
  }

  // Склад умеет три знака после запятой, но 1.000 в поле — визуальный мусор.
  formatQuantity(value) {
    return String(Number(value.toFixed(3)))
  }
}
